import { useAbTesting } from '@packages/abtesting';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Button, CircularProgress, DialogPreset, Link, Notification } from '@packages/shared';
import { useSession } from '@packages/utilities';
import { Basket32 } from '@packages/themes/icons';
import type { GtmEventAddToCart } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { mutate } from 'swr';
import { selectedQuantity, selectedServices } from '../../atoms';
import { useHashParameters } from '../../hooks';
import { AtbLayer } from './AtbLayer/AtbLayer';
import type { AddToBasketArgs } from './useAddToBasket';
import { useAddToBasket } from './useAddToBasket';
import { useBreadcrumbs } from './useBreadcrumbs';

const AddToBasketFragment = graphql(/* GraphQL */ `
  fragment AddToBasketFragment on Product {
    akl
    sku
    defaultCategory
    promotion
    longDescription
    name
    marketcode
    orderNumber
    media {
      type
      url
    }
    dimensions {
      axisType
      type
      values {
        displayText
        isSelected
      }
    }
    availability {
      message
      isOrderable
      state
    }
    price {
      value
      saving {
        value
      }
    }
    brand {
      name
    }
    ...AtbLayerFragment
  }
`);

const messages = defineMessages({
  soldOut: { id: 'add-to-basket.sold-out', defaultMessage: 'Ausverkauft' },
  available: { id: 'add-to-basket.label', defaultMessage: 'In den Warenkorb' },
  headline: {
    defaultMessage: 'In den Warenkorb gelegt',
    id: 'addToBasket.layer.headline',
  },
  warningMessage: {
    defaultMessage: 'Bitte wähle zuerst eine Variante aus!',
    id: 'addToBasket.error.warning',
  },
  errorMessage: {
    defaultMessage:
      'Dein Artikel konnte leider nicht in den Warenkorb gelegt werden. Bitte versuche es später nochmal',
    id: 'addToBasket.error.message',
  },
  continueShopping: {
    id: 'add-to-basket.modal.continue-shopping',
    defaultMessage: 'Weiter shoppen',
  },
  navigateToCart: {
    id: 'add-to-basket.modal.navigate-to-basket',
    defaultMessage: 'Zum Warenkorb',
  },
  toCheckout: {
    id: 'add-to-basket.modal.navigate-to-checkout',
    defaultMessage: 'Zur Kasse',
  },
});

export type AddToBasketButtonProps = {
  /**
   * addToBasket fragment data
   */
  productData: FragmentType<typeof AddToBasketFragment>;
};

export const AddToBasketButton = ({ productData }: AddToBasketButtonProps) => {
  const product = getFragmentData(AddToBasketFragment, productData);
  // these atoms has to be outsourced into a wrapper when AddToBasketButton gets migrated into modules
  // in future this component will also be used from domain order
  const quantity = useAtomValue(selectedQuantity);
  const serviceLineitems = useAtomValue(selectedServices);
  const { setDefaultProductOutcome } = useAbTesting();
  const {
    akl,
    availability: { state, isOrderable },
    media,
    sku,
    defaultCategory,
    longDescription,
    brand,
    promotion,
    orderNumber,
    price,
    dimensions,
    availability,
    name,
    marketcode,
  } = product;
  const [dialogOpened, setDialogOpened] = useState(false);
  const [openErrorNotification, setOpenErrorNotification] = useState(false);
  const [openWarningNotification, setOpenWarningNotification] = useState(false);
  const { formatMessage } = useIntl();
  const { jwtToken, loading, loggedIn } = useSession();
  const { category, searchNo } = useHashParameters();
  const categoryId = category || defaultCategory || undefined;
  const isAvailable = state !== 'UNSELECTED' ? isOrderable : true;
  const { query } = useRouter();

  const breadcrumbs = useBreadcrumbs(categoryId);
  const { trigger, cartData, isMutating } = useAddToBasket();
  const dispatchGTMEvent = useTracking();

  const size =
    dimensions
      ?.filter((dimension) => dimension?.type !== 'INPUT')
      .pop()
      ?.values.filter((value) => value.isSelected === true)
      .map((selectedValue) => selectedValue.displayText)
      .join('') ?? '';

  // tracking parameters for atb request
  // implementation according to https://empiriecom.atlassian.net/browse/INFORM-2117
  let trackingParameter: AddToBasketArgs['trackingParameter'];

  if (query['nav-q'] || searchNo) {
    trackingParameter = {
      source: 'Suche',
    };
  } else if (categoryId && breadcrumbs) {
    trackingParameter = {
      source: categoryId,
      breadcrumbs,
    };
  }

  const color =
    dimensions
      ?.filter((dimension) => dimension?.axisType === 'COLOR')
      .pop()
      ?.values.filter((value) => value.isSelected === true)
      .map((selectedValue) => selectedValue.displayText)
      .join('') ?? '';

  const variant = ([] as string[])
    .concat(
      ...dimensions.map((dimension) =>
        dimension.values
          .filter((dimObj) => dimObj.isSelected === true)
          .map((dimVal) => dimVal.displayText || ''),
      ),
    )
    .join(', ');

  const isLoading = loading || isMutating;

  useEffect(() => {
    if (dialogOpened && cartData) {
      if (!cartData.success) {
        setOpenErrorNotification(true);
        setDialogOpened(false);
      } else if (cartData.success) {
        dispatchGTMEvent({
          event: 'ga_event',
          eventAction: 'view',
          eventCategory: 'ATB_toCheckAndOrder',
          eventLabel: 'detailview_atb_modal',
        });
        setDefaultProductOutcome(
          [
            {
              count: quantity,
              masterId: akl,
              sku: `${orderNumber}${promotion}${size ? `-${size}` : ''}`,
              value: price.value / 100,
            },
          ],
          'ATB',
        );

        dispatchGTMEvent<GtmEventAddToCart>({
          Merkzettel: 'nein',
          ecommerce: {
            add: {
              products: [
                {
                  brand: brand?.name,
                  category: query['nav-q'] ? `search:${query['nav-q']}` : breadcrumbs?.join('/'),
                  description: longDescription || undefined,
                  dimension12: price.value > 0 ? 'nein' : 'ja',
                  dimension15: size,
                  dimension16: color,
                  dimension18: price.saving?.value! > 0 ? 'ja' : 'nein',
                  dimension19: promotion || undefined,
                  dimension20: availability.message,
                  dimension21: brand?.name,
                  id: orderNumber,
                  imageUrl: media[0].url || undefined,
                  marketcode: marketcode || undefined,
                  name,
                  price: price.value / 100,
                  quantity,
                  sku,
                  variant,
                },
              ],
            },
          },
          event: 'EECaddToCart',
        });
        dispatchGTMEvent({
          event: 'ga_event',
          eventAction: 'viewLayer',
          eventCategory: 'catalog_addedproduct',
          eventValue: {
            orderNumber: sku.concat(promotion || ''),
            price: price.value,
            size,
          },
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, dialogOpened]);

  const onClickHandler = async () => {
    if (state === 'UNSELECTED') {
      setOpenWarningNotification(true);
    } else if (cartData && !cartData.success) {
      setOpenErrorNotification(true);
    } else {
      try {
        await trigger({
          sku,
          count: quantity,
          serviceLineitems,
          trackingParameter,
          basketOption: [],
          isSpecialOrder: false,
        });
        // revalidate basket abstract data after successful basket update
        await mutate(jwtToken ? ['/api/checkout/v1/abstract', jwtToken] : null);
        setDialogOpened(true);
      } catch (err) {
        // TODO(error-handling): add service
        setOpenErrorNotification(true);
      }
    }
  };

  const trackToCheckAndOrderHandler = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_toBasket',
      eventLabel: 'detailview_atb_modal',
    });
  };

  const layerCloseHandler = () => {
    setDialogOpened(false);
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'click',
      eventCategory: 'ATB_continueShopping',
      eventLabel: 'detailview_atb_modal',
    });
  };

  return (
    <>
      {openWarningNotification && (
        <Notification
          open={openWarningNotification}
          severity="warning"
          autoHideDuration={5000}
          onClose={() => setOpenWarningNotification(false)}
        >
          {formatMessage(messages.warningMessage)}
        </Notification>
      )}
      {openErrorNotification && (
        <Notification
          open={openErrorNotification}
          severity="error"
          autoHideDuration={5000}
          onClose={() => setOpenErrorNotification(false)}
        >
          {!cartData?.success
            ? cartData?.successMessages.elements[0].description
            : formatMessage(messages.errorMessage)}
        </Notification>
      )}
      {dialogOpened && cartData && cartData.success && (
        <DialogPreset
          title={formatMessage(messages.headline)}
          maxWidth="lg"
          stickyFooter
          buttons={
            <>
              {loggedIn && (
                <Button
                  LinkComponent={Link}
                  onClick={trackToCheckAndOrderHandler}
                  href="/kasse/zahlung"
                  color="primary"
                  size="large"
                  sx={{ maxWidth: ['100%', '100%', '180px'] }}
                  fullWidth
                >
                  {formatMessage(messages.toCheckout)}
                </Button>
              )}
              <Button
                LinkComponent={Link}
                onClick={trackToCheckAndOrderHandler}
                href="/warenkorb"
                color={loggedIn ? 'inherit' : 'primary'}
                size="large"
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(messages.navigateToCart)}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={layerCloseHandler}
                sx={{ maxWidth: ['100%', '100%', '180px'] }}
                fullWidth
              >
                {formatMessage(messages.continueShopping)}
              </Button>
            </>
          }
          open={dialogOpened}
          onClose={layerCloseHandler}
        >
          <AtbLayer
            lineItems={cartData.newPLIs.length > 0 ? cartData.newPLIs : undefined}
            productData={product}
          />
        </DialogPreset>
      )}
      <Button
        endIcon={isLoading ? <CircularProgress color="secondary" size="s" /> : <Basket32 />}
        color="primary"
        size="large"
        onClick={onClickHandler}
        fullWidth
        sx={{ whiteSpace: 'nowrap' }}
        disabled={!isAvailable || isLoading}
      >
        {formatMessage(isAvailable ? messages.available : messages.soldOut)}
      </Button>
    </>
  );
};
